import {
  Color,
  DoubleSide,
  LoadingManager,
  Material,
  Mesh,
  MeshMatcapMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  type Object3D,
} from 'three'

export type WebMaterialRole = 'matcap' | 'image' | 'video' | 'emissive' | 'glass' | 'unknown'

export interface MaterialSummary {
  meshes: number
  assignments: number
  matcapMaterials: number
  imageMaterials: number
  emissiveMaterials: number
  videoMaterials: number
}

type MatcapProfile = 'toon' | 'room' | 'fabric' | 'terracotta'

interface MatcapUrls {
  readonly toon: string
  readonly room: string
  readonly fabric: string
  readonly terracotta: string
}

interface MatcapTextures {
  toon: Texture
  room: Texture
  fabric: Texture
  terracotta: Texture
}

interface MatcapOverride {
  readonly profile: MatcapProfile
  readonly color: number
}

const ANIME_PALETTE: Readonly<Record<string, number>> = {
  antique_brass: 0xc3914d,
  bedroom_coral_headboard_wall: 0xcf746b,
  bedroom_deep_teal_trim: 0x214751,
  bedroom_floor_seams: 0x4a312c,
  bedroom_fluffy_quilt_teal: 0x358991,
  bedroom_honey_oak_floor: 0xa76b46,
  bedroom_quilt_cream_stitching: 0xbccbbd,
  bedroom_quilt_folded_band: 0xc9a56f,
  bedroom_soft_sage_wall: 0x879c8b,
  appliance_white: 0xd9ddd9,
  box_paper: 0xa47b4c,
  car_key_fob: 0x252a35,
  car_key_logo: 0xdde4e8,
  car_key_logo_cover: 0x303947,
  car_key_metal: 0x9aa8b8,
  car_key_ring: 0x8996a5,
  book_cover_lettering: 0xf0ad47,
  cabinet_cream: 0xc2b89e,
  coffee: 0x493126,
  fabric_scarf: 0x8ea0b1,
  food_yellow: 0xe9b449,
  gameboy_button_blue: 0x3475d9,
  gameboy_button_green: 0x45a866,
  gameboy_button_red: 0xd94a4f,
  gameboy_button_yellow: 0xe7b841,
  gameboy_dark: 0x2b3039,
  gameboy_screen: 0x43535c,
  gameboy_shell: 0xc8bfa7,
  gameboy_trim: 0xc5b998,
  kitchen_deep_green_trim: 0x254c3e,
  kitchen_floor_bluegray: 0x657b7f,
  kitchen_red: 0xc7433e,
  kitchen_tile_grout: 0xa8aea7,
  kitchen_wall_warm_white: 0xcac0ad,
  leaf_green: 0x4f8c54,
  light_oak: 0xb97842,
  mug_ceramic: 0xf0e7d8,
  mug_heart: 0xe75d6c,
  mug_lettering: 0xc70518,
  pale_quartz: 0xc9c3b5,
  perfume_detail: 0x75364a,
  sage_cabinet: 0x62816d,
  sweater_letter_black: 0x090909,
  warm_white_ceramic: 0xe7dcc5,
  bench_buttons: 0x26384f,
  bench_dusty_blue: 0x4b6684,
  brushed_steel: 0x778596,
  dark_walnut: 0x4a302d,
  // The exported "Wood word" prop uses MATCAP_Decor_LOVE. Keep its colour
  // isolated here so a dedicated red matcap texture can replace this tint later.
  decor_love: 0xd93646,
  door_dark: 0x35323a,
  lamp_ceramic_blue: 0x315a78,
  lamp_linen: 0xc1ad8c,
  matte_black: 0x2d3340,
  mattress_ivory: 0xd4cdbf,
  mattress_piping: 0x7f938e,
  mattress_white: 0xe3e0d6,
  media_console_cream: 0xc2b18f,
  pillow_button: 0x9e8a70,
  pillow_cotton: 0xe2d8c4,
  rug_cream_border: 0xb9a482,
  rug_dusty_blue: 0x4e7095,
  rug_tassels: 0x8b7659,
  tv_feet: 0x303947,
  tv_frame: 0x222936,
  toy_car_body: 0xcf4d59,
  toy_car_chrome: 0x9aa8b8,
  toy_car_headlight: 0xffdc78,
  toy_car_indicator: 0xff8b28,
  toy_car_interior: 0x3a2f37,
  toy_car_plate: 0xded2ad,
  toy_car_rubber: 0x252a35,
  toy_car_seats: 0xb8784c,
  toy_car_tail_light: 0xef3f4c,
  toy_car_trim: 0x303745,
  mini_moto_body: 0xe44745,
  mini_moto_dark: 0x252a34,
  mini_moto_light: 0xece1bd,
  mini_moto_metal: 0x8e9aa8,
  mini_moto_tail_cover: 0x5a2731,
  mini_moto_tail_light: 0xff4650,
  mini_moto_wood: 0xb36f34,
  newtons_cradle_balls: 0xb6c3cf,
  newtons_cradle_base: 0x713a35,
  newtons_cradle_frame: 0x8795a6,
  newtons_cradle_strings: 0x2d3340,
  slippers_sole: 0x2c3340,
  slippers_upper: 0xc88a70,
  vinyl_record: 0x252833,
  wall_art_canvas: 0xc7bfb0,
  wall_art_frame: 0x896244,
  wall_clock_black: 0x292e3a,
  wall_clock_grey: 0x626c79,
  wall_clock_metal: 0x8b98a6,
  wall_clock_second_hand: 0xe44e52,
  wardrobe_sage: 0x587562,
  warm_oak: 0xa86b43,
  watch_dark_detail: 0x303744,
  watch_light_detail: 0xaeb9c3,
  watch_metal: 0x8895a1,
}

// Broad, flat surfaces expose the camera-space gradients of a hard cel matcap.
// A softer profile keeps those surfaces calm while props retain the toon bands.
const ROOM_MATERIAL_KEYS = new Set([
  'bedroom_coral_headboard_wall',
  'bedroom_deep_teal_trim',
  'bedroom_floor_seams',
  'bedroom_honey_oak_floor',
  'bedroom_soft_sage_wall',
  'cabinet_cream',
  'kitchen_deep_green_trim',
  'kitchen_floor_bluegray',
  'kitchen_tile_grout',
  'kitchen_wall_warm_white',
  'pale_quartz',
  'sage_cabinet',
])

const FABRIC_MATERIAL_KEYS = new Set(['fabric_scarf'])

// The broken pot is authored as a realistic image material so its interaction
// node stays editable in Blender. At runtime, replace that one stable material
// key with a soft clay matcap chosen for readable terracotta edges without a
// glossy/plastic highlight. Future GLB exports keep picking up this override.
const MATCAP_OVERRIDES: Readonly<Record<string, MatcapOverride>> = {
  brokenpot_material: {
    profile: 'terracotta',
    color: 0xffffff,
  },
}

const inferRole = (material: Material): WebMaterialRole => {
  const explicitRole = material.userData.web_role
  if (typeof explicitRole === 'string') return explicitRole as WebMaterialRole
  if (material.name.startsWith('MATCAP_')) return 'matcap'
  if (material.name.startsWith('IMAGE_')) return 'image'
  if (material.name.startsWith('VIDEO_')) return 'video'
  if (material.name.startsWith('EMISSIVE_')) return 'emissive'
  if (material.name.startsWith('GLASS_')) return 'glass'
  return 'unknown'
}

const materialKey = (material: Material): string => {
  const explicitKey = material.userData.web_material_key
  if (typeof explicitKey === 'string') return explicitKey
  return material.name.replace(/^MATCAP_/, '').toLowerCase()
}

const sourceColor = (material: Material): Color => {
  const colorValue = (material as Material & { color?: Color }).color
  if (colorValue instanceof Color) return colorValue.clone()
  return new Color(0xffffff)
}

export class MaterialManager {
  private readonly loader: TextureLoader
  private readonly matcapUrls: MatcapUrls
  private readonly maxAnisotropy: number
  private matcapTextures: MatcapTextures | null = null
  private loadPromise: Promise<MatcapTextures> | null = null
  private readonly replacements = new Map<string, Material>()

  public constructor(manager: LoadingManager, matcapUrls: MatcapUrls, maxAnisotropy: number) {
    this.loader = new TextureLoader(manager)
    this.matcapUrls = matcapUrls
    this.maxAnisotropy = maxAnisotropy
  }

  public load(): Promise<MatcapTextures> {
    if (this.matcapTextures) return Promise.resolve(this.matcapTextures)
    if (this.loadPromise) return this.loadPromise

    this.loadPromise = Promise.all([
      this.loader.loadAsync(this.matcapUrls.toon),
      this.loader.loadAsync(this.matcapUrls.room),
      this.loader.loadAsync(this.matcapUrls.fabric),
      this.loader.loadAsync(this.matcapUrls.terracotta),
    ]).then(([toon, room, fabric, terracotta]) => {
      toon.name = 'AnimeToonMatcap'
      room.name = 'AnimeRoomSoftMatcap'
      fabric.name = 'AnimeFabricSoftMatcap'
      terracotta.name = 'AnimeTerracottaClayMatcap'

      for (const texture of [toon, room, fabric, terracotta]) {
        texture.colorSpace = SRGBColorSpace
        texture.anisotropy = this.maxAnisotropy
        texture.userData.runtime_shared_texture = true
        texture.needsUpdate = true
      }

      this.matcapTextures = { toon, room, fabric, terracotta }
      return this.matcapTextures
    })
    return this.loadPromise
  }

  public apply(root: Object3D): MaterialSummary {
    if (!this.matcapTextures) throw new Error('MaterialManager.load() must finish before apply().')

    const retiredMaterials = new Set<Material>()
    const uniqueRoles = new Map<string, WebMaterialRole>()
    let meshes = 0
    let assignments = 0

    root.traverse((object) => {
      if (!(object instanceof Mesh)) return
      meshes += 1
      object.castShadow = false
      object.receiveShadow = false

      const originals = Array.isArray(object.material) ? object.material : [object.material]
      const replacements = originals.map((material) => {
        assignments += 1
        const role = inferRole(material)
        uniqueRoles.set(material.uuid, role)
        const key = materialKey(material)
        const matcapOverride = MATCAP_OVERRIDES[key]

        if (matcapOverride) {
          retiredMaterials.add(material)
          uniqueRoles.set(material.uuid, 'matcap')
          return this.getMatcapMaterial(material, matcapOverride)
        }

        if (role === 'matcap') {
          retiredMaterials.add(material)
          if (materialKey(material) === 'lamp_linen') {
            return this.getLampGlowMaterial(material)
          }
          return this.getMatcapMaterial(material)
        }

        if (role === 'glass') {
          retiredMaterials.add(material)
          return this.getGlassMaterial(material)
        }

        if (role === 'image' && material instanceof MeshStandardMaterial) {
          for (const texture of [material.map, material.normalMap]) {
            if (!texture) continue
            texture.anisotropy = this.maxAnisotropy
            texture.needsUpdate = true
          }

          const key = materialKey(material)
          if (key === 'sweater_fabric' && material.map) {
            retiredMaterials.add(material)
            return this.getSweaterFabricMaterial(material)
          }

          material.metalness = 0
          material.roughness = 0.88
          if (key === 'wall_art_vectorial' && material.map) {
            // Use a restrained copy of the artwork as an emissive map. The
            // area lights still shape it, while the line art remains legible
            // from every orbit angle instead of sinking into the night wall.
            material.emissive.set(0xffffff)
            material.emissiveMap = material.map
            material.emissiveIntensity = 0.42
          }
          material.needsUpdate = true
        }

        if (role === 'emissive') {
          material.toneMapped = false
          if (
            materialKey(material) === 'wall_clock_backlight'
            && material instanceof MeshStandardMaterial
          ) {
            material.emissive.set(0x72baff)
            material.emissiveIntensity = 4.6
            material.color.set(0x2f6fa8)
            // The source neon strip includes wall-facing polygons. Rendering
            // both sides makes its blue edge glow visible from the room.
            material.side = DoubleSide
          }
          material.needsUpdate = true
        }
        return material
      })

      object.material = Array.isArray(object.material) ? replacements : replacements[0]
    })

    retiredMaterials.forEach((material) => material.dispose())

    const roles = [...uniqueRoles.values()]
    return {
      meshes,
      assignments,
      matcapMaterials: roles.filter((role) => role === 'matcap').length,
      imageMaterials: roles.filter((role) => role === 'image').length,
      emissiveMaterials: roles.filter((role) => role === 'emissive').length,
      videoMaterials: roles.filter((role) => role === 'video').length,
    }
  }

  private getMatcapMaterial(
    source: Material,
    override?: MatcapOverride,
  ): MeshMatcapMaterial {
    const existing = this.replacements.get(source.uuid)
    if (existing instanceof MeshMatcapMaterial) return existing

    const key = materialKey(source)
    const profile: MatcapProfile = override?.profile ?? (
      FABRIC_MATERIAL_KEYS.has(key)
        ? 'fabric'
        : ROOM_MATERIAL_KEYS.has(key)
          ? 'room'
          : 'toon'
    )
    const color = new Color(
      override?.color ?? ANIME_PALETTE[key] ?? sourceColor(source).getHex(),
    )
    const replacement = new MeshMatcapMaterial({
      color,
      matcap: this.matcapTextures?.[profile] ?? null,
      side: source.side,
      transparent: source.transparent,
      opacity: source.opacity,
      fog: true,
    })
    replacement.name = source.name
    replacement.userData = {
      ...source.userData,
      web_role: 'matcap',
      web_material_key: key,
      runtime_material: 'MeshMatcapMaterial',
      runtime_matcap_profile: profile,
    }
    this.replacements.set(source.uuid, replacement)
    return replacement
  }

  private getGlassMaterial(source: Material): MeshMatcapMaterial {
    const existing = this.replacements.get(source.uuid)
    if (existing instanceof MeshMatcapMaterial) return existing

    const replacement = new MeshMatcapMaterial({
      color: 0x9cb8d0,
      matcap: this.matcapTextures?.toon ?? null,
      transparent: true,
      opacity: 0.24,
      depthWrite: false,
      side: source.side,
      fog: true,
    })
    replacement.name = source.name
    replacement.userData = { ...source.userData, runtime_material: 'AnimeMatcapGlass' }
    this.replacements.set(source.uuid, replacement)
    return replacement
  }

  private getSweaterFabricMaterial(source: MeshStandardMaterial): MeshPhysicalMaterial {
    const existing = this.replacements.get(source.uuid)
    if (existing instanceof MeshPhysicalMaterial) return existing

    // The color map contains the user's texture paint and the normal map holds
    // the authored knit detail. A rough physical surface plus broad sheen adds
    // the soft edge response of fabric without changing either texture.
    const replacement = new MeshPhysicalMaterial({
      color: 0xffffff,
      map: source.map,
      lightMap: source.lightMap,
      lightMapIntensity: source.lightMapIntensity,
      aoMap: source.aoMap,
      aoMapIntensity: source.aoMapIntensity,
      emissive: 0xffffff,
      emissiveMap: source.map,
      emissiveIntensity: 0.12,
      bumpMap: source.bumpMap,
      bumpScale: source.bumpScale,
      normalMap: source.normalMap,
      normalMapType: source.normalMapType,
      normalScale: source.normalScale.clone(),
      displacementMap: source.displacementMap,
      displacementScale: source.displacementScale,
      displacementBias: source.displacementBias,
      roughness: 0.94,
      roughnessMap: source.roughnessMap,
      metalness: 0,
      alphaMap: source.alphaMap,
      sheen: 1,
      sheenColor: 0xfff1d6,
      sheenRoughness: 0.82,
      side: source.side,
      transparent: source.transparent,
      opacity: source.opacity,
      alphaTest: source.alphaTest,
      depthWrite: source.depthWrite,
      depthTest: source.depthTest,
      vertexColors: source.vertexColors,
      fog: source.fog,
    })
    replacement.name = source.name
    replacement.toneMapped = source.toneMapped
    replacement.userData = {
      ...source.userData,
      web_role: 'image',
      web_material_key: 'sweater_fabric',
      runtime_material: 'MeshPhysicalMaterial',
      runtime_finish: 'rough-sheen-fabric',
    }
    this.replacements.set(source.uuid, replacement)
    return replacement
  }

  private getLampGlowMaterial(source: Material): MeshStandardMaterial {
    const existing = this.replacements.get(source.uuid)
    if (existing instanceof MeshStandardMaterial) return existing

    const replacement = new MeshStandardMaterial({
      color: 0xffd9a3,
      emissive: 0xffad55,
      emissiveIntensity: 2.15,
      metalness: 0,
      roughness: 0.82,
      side: source.side,
      transparent: source.transparent,
      opacity: source.opacity,
      fog: true,
    })
    replacement.name = source.name
    replacement.userData = {
      ...source.userData,
      web_role: 'emissive',
      web_material_key: 'lamp_linen',
      runtime_material: 'WarmLampGlowMaterial',
    }
    this.replacements.set(source.uuid, replacement)
    return replacement
  }

  /** Drop cache references after a scene has already disposed those materials. */
  public forgetDisposedMaterials(materials: ReadonlySet<Material>): void {
    if (materials.size === 0) return
    for (const [sourceId, replacement] of this.replacements) {
      if (materials.has(replacement)) this.replacements.delete(sourceId)
    }
  }

  public dispose(): void {
    this.replacements.forEach((material) => material.dispose())
    this.replacements.clear()
    this.matcapTextures?.toon.dispose()
    this.matcapTextures?.room.dispose()
    this.matcapTextures?.fabric.dispose()
    this.matcapTextures?.terracotta.dispose()
    this.matcapTextures = null
    this.loadPromise = null
  }
}
